import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@components/providers/ErrorBoundary';
import { ProtectedRoute } from '@components/providers/ProtectedRoute';
import { LoadingSpinner } from '@components/atoms/LoadingSpinner';

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@pages/Login/LoginPage'));
const AanmeldenPage = lazy(() => import('@pages/Dashboard/AanmeldenPage'));
const BeantwoordenPage = lazy(() => import('@pages/Dashboard/BeantwoordenPage'));
const BesprekenPage = lazy(() => import('@pages/Dashboard/BesprekenPage'));
const SessiesPage = lazy(() => import('@pages/Sessies/SessiesPage'));
const SessieAanmakenPage = lazy(() => import('@pages/Sessies/SessieAanmakenPage'));
const SessieLivePage = lazy(() => import('@pages/Sessies/SessieLivePage'));
const RapportPage = lazy(() => import('@pages/Rapportage/RapportPage'));
const ParticipantJoinPage = lazy(() => import('@pages/Deelnemer/DeelnemerJoinPage'));
const ParticipantTokenPage = lazy(() => import('@pages/Deelnemer/DeelnemerTokenPage'));
const ParticipantPage = lazy(() => import('@pages/Deelnemer/DeelnemerPage'));
const GebruikersPage = lazy(() => import('@pages/Admin/GebruikersPage'));
const OrganisatiesPage = lazy(() => import('@pages/Admin/OrganisatiesPage'));
const SjablonenPage = lazy(() => import('@pages/Admin/SjablonenPage'));

// Fallback component for lazy loading
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner size="lg" />
  </div>
);

// Main App component with routing
function App() {
  return (
    <ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Er is een fout opgetreden</h1>
        <p className="text-gray-600">Vernieuw de pagina om het opnieuw te proberen.</p>
      </div>
    </div>}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Deelnemer Routes (geen auth vereist) */}
          <Route path="/deelnemer" element={<ParticipantTokenPage />} />
          <Route path="/deelnemen/:sessieCode" element={<ParticipantJoinPage />} />
          <Route path="/deelnemer/:token" element={<ParticipantPage />} />
          
          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            {/* Dashboard = Projectie scherm (voor deelnemers te zien) */}
            <Route path="/dashboard/:sessieCode/aanmelden" element={<AanmeldenPage />} />
            <Route path="/dashboard/:sessieCode/beantwoorden" element={<BeantwoordenPage />} />
            <Route path="/dashboard/:sessieCode/bespreken" element={<BesprekenPage />} />

            {/* Sessies = Beheer omgeving (voor facilitator op laptop) */}
            <Route path="/sessies" element={<SessiesPage />} />
            <Route path="/sessies/aanmaken" element={<SessieAanmakenPage />} />
            <Route path="/sessies/:sessieCode" element={<SessieLivePage />} />

            {/* Sjablonen Routes */}
            <Route path="/sjablonen" element={<SjablonenPage />} />

            {/* Rapportage Routes */}
            <Route path="/rapportages/:sessieCode" element={<RapportPage />} />

            {/* Admin Routes */}
            <Route path="/admin/gebruikers" element={<GebruikersPage />} />
            <Route path="/admin/organisaties" element={<OrganisatiesPage />} />
          </Route>
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/sessies" replace />} />
          <Route path="*" element={<Navigate to="/sessies" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
